import { type PortalMessage } from "../../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * `Organization` → Sharing — `/organization/sharing` (plan-014 B5; design §27.15.1; FR12-SHR-01…05). Drawn to the
 * prototype's `sharing` page (`design/WeTalk.dc.html`, the Organization block renderer): kicker ("… · between
 * workspaces of this organization"), "Sharing between workspaces", the lede, the tab strip, then —
 *
 *   Shared now        every share the person may see: the file or the agent's configuration, its fixed revision,
 *                     from → to, who uses it (or who has not accepted yet), and the acts the person holds — Accept
 *                     (for a workspace it is offered to) and Revoke (the source, or the organization); "Share
 *                     something" opens the choice (from which workspace, what, with whom)
 *                     + the always-combined do-not-call row ("Every workspace adds to it and every campaign checks
 *                     all of it. If it cannot be read, outbound calls stop.") with the list's size
 *   Never shared      the notice: recordings, transcripts, caller details, API keys, SIP passwords, cards, invoices
 *
 * EVERY AFFORDANCE IS REAL (FR12-SCR-04) and decided by the API; the page offers an act only where the read says the
 * person holds it (publishable / acceptable workspaces). A revocation is answered with who keeps the revision — the
 * prototype's "Trastevere keeps revision 4 in its live version but cannot build with it again." Styles: the shared
 * renderer's (`../page/organization-page.css`) plus `./account-sharing.css`, tokens only (ADR 0018).
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import { Button, Dialog, Field, Select } from "@wetalk/ui";
import { useState, type FormEvent, type ReactNode } from "react";

import { apiProblemText } from "../../../api/api-error.js";
import { useGateway } from "../../../api/provider.js";
import type {
  ResourceShareOutcomeView,
  ResourceShareView,
  ResourceSharingView,
  ShareableResourceView,
} from "../../../api/slice/resource-share.js";
import { GLYPH } from "../../../shell/glyph.js";
import { roleHolds, useOpenAccount } from "../overview/account-overview-screen.js";
import { Block, BlockNote, ItemColumn, ItemRow, Notice, OrganizationPage, type Outcome } from "../page/organization-page.js";
import { shortDateText } from "../page/role-copy.js";

import "./account-sharing.css";

/** The page's search parameters. None: the page has one view. */
export interface AccountSharingSearch {}

/**
 * The route's `validateSearch`: a total function over the untrusted address bar, so a hand-edited URL still opens a
 * valid page.
 */
export function accountSharingSearchOf(_raw: Readonly<Record<string, unknown>>): AccountSharingSearch {
  return {};
}

export interface AccountSharingScreenProps {
  readonly search: AccountSharingSearch;
  readonly onSearch: (next: AccountSharingSearch) => void;
}

export const accountSharingKey = (accountId: string) => ["resource-share", accountId] as const;

// ── the words ─────────────────────────────────────────────────────────────────────────────────────────────

/** "Revision 4". */
export function revisionWord(ordinal: number | null): string {
  return ordinal === null ? portalText("core", "account_admin.revision_3beb9a") : portalText("core", "account_admin.revision_36c8a1", { value0: String(ordinal) });
}

/** The row's title: the file name, or "<agent> — configuration". */
export function shareTitle(share: Pick<ResourceShareView, "resourceKind" | "label">): string {
  if (share.resourceKind === "agent_document") return share.label ?? portalText("core", "account_admin.a_document_6d50dd");
  return portalText("core", "account_admin._configuration_312d4a", { value0: share.label ?? portalText("core", "account_admin.an_agent_d95187") });
}

/** "A, B and C". */
function joined(names: readonly string[]): string {
  if (names.length <= 1) return names.join("");
  return portalText("core", "account_admin._and_4142c0", { value0: names.slice(0, -1).join(", "), value1: names[names.length - 1] ?? "" });
}

/** The short name a workspace goes by inside its organization ("Roma — Trastevere" → "Trastevere"). */
export function shortWorkspaceName(name: string): string {
  const cut = name.lastIndexOf(" — ");
  return cut === -1 ? name : name.slice(cut + 3);
}

/** What the row says under its title: from → to, then who uses it, waits, or that it was revoked. */
export function shareDek(share: ResourceShareView): string {
  const route = portalText("core", "account_admin.from_to_4bf53a", { value0: share.sourceWorkspace.name, value1: share.destinationWorkspace?.name ?? portalText("core", "account_admin.every_workspace_6e0846") });
  if (share.state === "revoked") return portalText("core", "account_admin._revoked_what_is_already_built_keeps_it_nothing_new_can_use_it_c95023", { value0: route });
  if (share.consumer.length === 0) return portalText("core", "account_admin._waiting_for_to_accept_f9979c", { value0: route, value1: share.destinationWorkspace === null ? portalText("core", "account_admin.a_workspace_b7c8a4") : shortWorkspaceName(share.destinationWorkspace.name) });
  if (share.resourceKind === "voice_agent_configuration") {
    const copies = share.consumer.flatMap((one) => one.inUse.map((use) => use.voiceAgentName));
    return copies.length === 0 ? portalText("core", "account_admin._accepted_no_agent_copied_from_it_yet_110928", { value0: route }) : portalText("core", "account_admin._copied_as_dfe8db", { value0: route, value1: joined(copies) });
  }
  const uses = share.consumer.flatMap((one) => one.inUse.filter((use) => use.isLiveVersion).map((use) => portalText("core", "account_admin._843a2d", { value0: use.voiceAgentName, value1: use.agentVersionLabel ?? "" }).trim()));
  return uses.length === 0 ? portalText("core", "account_admin._accepted_not_in_a_live_agent_yet_1b4da2", { value0: route }) : portalText("core", "account_admin._used_by_916588", { value0: route, value1: joined(uses) });
}

/** The right-hand moment: "accepted 12 Sep", "shared 5 Oct", "revoked 6 Oct". */
export function shareMeta(share: ResourceShareView): string {
  if (share.state === "revoked" && share.revokedAt !== null) return portalText("core", "account_admin.revoked_96f656", { value0: shortDateText(share.revokedAt) });
  if (share.acceptedAt !== null) return portalText("core", "account_admin.accepted_8da7e9", { value0: shortDateText(share.acceptedAt) });
  return portalText("core", "account_admin.shared_2dd315", { value0: shortDateText(share.publishedAt) });
}

/**
 * The answer to a revocation, naming who keeps the revision — the prototype's "Revoked. Trastevere keeps revision 4 in
 * its live version but cannot build with it again."
 */
export function revocationText(outcome: ResourceShareOutcomeView): string {
  if (outcome.outcome === "already_revoked") return portalText("core", "account_admin.that_was_already_revoked_nothing_new_can_use_it_a1d286");
  const revision = outcome.revisionOrdinal === null ? portalText("core", "account_admin.this_revision_730af8") : portalText("core", "account_admin.revision_8e0836", { value0: String(outcome.revisionOrdinal) });
  if (outcome.resourceKind === "voice_agent_configuration") {
    const copied = outcome.consumer.filter((one) => one.copiedResourceId !== null).map((one) => shortWorkspaceName(one.name));
    return copied.length === 0
      ? portalText("core", "account_admin.revoked_no_workspace_can_make_a_copy_of_it_now_e40ea9")
      : portalText("core", "account_admin.revoked_no_new_copies_can_be_made_6fa0f6", { value0: joined(copied), value1: copied.length === 1 ? portalText("core", "account_admin.keeps_the_copy_it_made_d0ca62") : portalText("core", "account_admin.keep_the_copies_they_made_1ae5f6") });
  }
  const keeping = outcome.consumer.filter((one) => one.inUse.some((use) => use.isLiveVersion)).map((one) => shortWorkspaceName(one.name));
  if (keeping.length > 0) {
    return portalText("core", "account_admin.revocation_kept", { count: keeping.length, workspaces: joined(keeping), revision });
  }
  const accepted = outcome.consumer.map((consumer) => shortWorkspaceName(consumer.name));
  return accepted.length === 0
    ? portalText("core", "account_admin.revoked_nobody_had_accepted_it_so_nothing_changes_anywhere_else_2abee1")
    : portalText("core", "account_admin.revoked_cannot_build_with_again_1dd5d4", { value0: joined(accepted), value1: revision });
}

/** The answer to an acceptance. */
export function acceptanceText(outcome: ResourceShareOutcomeView, consumerName: string): string {
  const who = shortWorkspaceName(consumerName);
  if (outcome.outcome === "already_accepted") return portalText("core", "account_admin._had_already_accepted_it_db3409", { value0: who });
  const revision = outcome.revisionOrdinal === null ? portalText("core", "account_admin.the_fixed_revision_0ac8e6") : portalText("core", "account_admin.revision_8e0836", { value0: String(outcome.revisionOrdinal) });
  return outcome.resourceKind === "agent_document"
    ? portalText("core", "account_admin.accepted_can_add_of_to_an_agent_s_documents_a_later_edit_at_the_s_90b9a9", { value0: who, value1: revision, value2: outcome.label ?? portalText("core", "account_admin.the_document_c85fa9") })
    : portalText("core", "account_admin.accepted_has_a_fixed_copy_of_to_start_a_new_agent_from_d5aadd", { value0: who, value1: revision });
}

/** The workspaces a share is offered to that the person may accept for and that have not accepted yet. */
export function acceptableFor(share: ResourceShareView, view: ResourceSharingView): readonly { readonly workspaceId: string; readonly name: string }[] {
  if (share.state === "revoked") return [];
  const accepted = new Set(share.consumer.map((one) => one.workspaceId));
  return view.workspace.filter(
    (one) =>
      one.state === "active" &&
      one.workspaceId !== share.sourceWorkspace.workspaceId &&
      (share.destinationWorkspace === null || share.destinationWorkspace.workspaceId === one.workspaceId) &&
      view.acceptableWorkspaceId.includes(one.workspaceId) &&
      !accepted.has(one.workspaceId),
  );
}

// ── the page ──────────────────────────────────────────────────────────────────────────────────────────────

export function AccountSharingScreen(_props: AccountSharingScreenProps): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const open = useOpenAccount();
  const accountId = open.accountId;
  const sharing = useQuery({
    queryKey: accountSharingKey(accountId ?? "none"),
    queryFn: () => gateway.readResourceSharing(accountId ?? ""),
    enabled: accountId !== null,
    retry: false,
  });
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [sharingOpen, setSharingOpen] = useState(false);

  const change = async (act: () => Promise<PortalMessage>): Promise<boolean> => {
    try {
      const text = await act();
      setOutcome({ tone: "done", text });
      await queryClient.invalidateQueries({ queryKey: ["resource-share"] });
      return true;
    } catch (raised) {
      setOutcome({ tone: "problem", get text() { return apiProblemText(raised, portalText("core", "account_admin.that_change_could_not_be_made_0e975e")); } });
      return false;
    }
  };

  const page = (children: ReactNode): ReactNode => (
    <OrganizationPage
      tab="sharing"

      title={portalText("core", "account_admin.sharing_between_workspaces_ff20b6")}
      lede={portalText("core", "account_admin.a_workspace_publishes_something_and_another_accepts_it_the_receiv_834c1e")}
      outcome={outcome}
    >
      {children}
    </OrganizationPage>
  );

  if (open.loading) return page(<BlockNote>{portalText("core", "account_admin.loading_the_organization_c14ce2")}</BlockNote>);
  if (accountId === null) return page(<BlockNote>{portalText("core", "account_admin.choose_an_organization_first_066988")}</BlockNote>);
  if (sharing.isPending) return page(<BlockNote>{portalText("core", "account_admin.loading_what_is_shared_a19959")}</BlockNote>);
  if (sharing.isError) {
    const refused = sharing.error instanceof ApiError && (sharing.error.status === 403 || sharing.error.status === 404);
    return page(
      <BlockNote>
        {refused
          ? portalText("core", "account_admin.only_an_owner_or_an_admin_of_the_organization_or_of_a_workspace_s_88e344")
          : apiProblemText(sharing.error, portalText("core", "account_admin.what_is_shared_could_not_be_loaded_f032d0"))}
      </BlockNote>,
    );
  }

  const view = sharing.data;
  const canManage = roleHolds(open.accountRole, "account.workspace.manage");
  const canShare = view.publishableWorkspaceId.length > 0 && view.shareable.length > 0;
  return page(
    <>
      <Block
        title={portalText("core", "account_admin.shared_now_10e05e")}
        buttons={
          canShare ? (
            <Button variant="primary" onClick={() => setSharingOpen(true)}>{portalText("core", "account_admin.share_something_39acda")}</Button>
          ) : undefined
        }
      >
        <ItemColumn label={portalText("core", "account_admin.shared_now_10e05e")}>
          {view.share.length === 0 ? (
            <BlockNote>{portalText("core", "account_admin.nothing_is_shared_yet_a_workspace_shares_a_document_or_an_agent_s_bc12f2")}</BlockNote>
          ) : null}
          {view.share.map((share) => (
            <ShareRow
              key={share.resourceShareId}
              share={share}
              view={view}
              canRevoke={share.state !== "revoked" && (canManage || view.publishableWorkspaceId.includes(share.sourceWorkspace.workspaceId))}
              onAccept={(workspace) =>
                change(async () => { const accepted = await gateway.acceptResourceShare(share.resourceShareId, workspace.workspaceId); return () => acceptanceText(accepted, workspace.name); })
              }
              onRevoke={() => change(async () => { const revoked = await gateway.revokeResourceShare(share.resourceShareId); return () => revocationText(revoked); })}
            />
          ))}
          <ItemRow
            glyph={GLYPH.prohibit}
            tone="caution"
            title={portalText("core", "account_admin.do_not_call_list_352681")}
            tag={portalText("core", "account_admin.always_combined_ebde49")}
            dek={portalText("core", "account_admin.every_workspace_adds_to_it_and_every_campaign_checks_all_of_it_if_62fd06")}
            meta={view.doNotCallEntryCount === 1 ? portalText("core", "account_admin.1_number_cf4532") : portalText("core", "account_admin._numbers_e2ca2a", { value0: String(view.doNotCallEntryCount) })}
          />
        </ItemColumn>
      </Block>
      <Block>
        <Notice
          tone="neutral"
          glyph={GLYPH.shieldCheck}
          title={portalText("core", "account_admin.never_shared_2f1aa0")}
          text={portalText("core", "account_admin.recordings_transcripts_caller_details_api_keys_sip_passwords_card_15ec6a")}
        />
      </Block>
      <ShareDialog
        open={sharingOpen}
        view={view}
        onClose={() => setSharingOpen(false)}
        onShare={async (from, chosen, destination) => {
          const done = await change(async () => {
            const shared = await gateway.shareResource(from, {
              resourceKind: chosen.resourceKind,
              resourceId: chosen.resourceId,
              destinationWorkspaceId: destination?.workspaceId ?? null,
            });
            const to = destination === null ? portalText("core", "account_admin.every_other_workspace_b25295") : shortWorkspaceName(destination.name);
            return () => (shared.outcome === "already_published"
              ? portalText("core", "account_admin._is_already_shared_with_d074d7", { value0: shareTitle(shared), value1: destination === null ? portalText("core", "account_admin.every_workspace_6e0846") : shortWorkspaceName(destination.name) })
              : portalText("core", "account_admin.shared_of_can_accept_it_now_141b16", { value0: revisionWord(shared.revisionOrdinal).toLowerCase(), value1: shareTitle(shared), value2: to }));
          });
          if (done) setSharingOpen(false);
        }}
      />
    </>,
  );
}

function ShareRow(props: {
  readonly share: ResourceShareView;
  readonly view: ResourceSharingView;
  readonly canRevoke: boolean;
  readonly onAccept: (workspace: { readonly workspaceId: string; readonly name: string }) => Promise<boolean>;
  readonly onRevoke: () => Promise<boolean>;
}): ReactNode {
  usePortalTranslation("core");
  const { share } = props;
  const [busy, setBusy] = useState(false);
  // plan-014 review I-20: revoking cannot be undone — it stops every consuming workspace building with the revision —
  // so it is confirmed first.
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);
  const acceptable = acceptableFor(share, props.view);
  const run = (act: () => Promise<boolean>): void => {
    setBusy(true);
    void act().finally(() => setBusy(false));
  };
  const actions =
    acceptable.length === 0 && !props.canRevoke ? undefined : (
      <span className="shr-row-actions">
        {acceptable.map((workspace) => (
          <Button key={workspace.workspaceId} variant="primary" disabled={busy} onClick={() => run(() => props.onAccept(workspace))}>
            {acceptable.length === 1 ? portalText("core", "account_admin.accept_bb54db") : portalText("core", "account_admin.accept_for_51b0a3", { value0: shortWorkspaceName(workspace.name) })}
          </Button>
        ))}
        {props.canRevoke ? (
          <Button variant="secondary" disabled={busy} onClick={() => setConfirmingRevoke(true)}>{portalText("core", "account_admin.revoke_0be720")}</Button>
        ) : null}
      </span>
    );
  return (
    <>
    <ItemRow
      glyph={share.resourceKind === "agent_document" ? GLYPH.fileText : GLYPH.squaresFour}
      tone={share.state === "revoked" ? "neutral" : "accent"}
      title={shareTitle(share)}
      tag={share.state === "revoked" ? portalText("core", "account_admin.revoked_9b9e04", { value0: revisionWord(share.revisionOrdinal).toLowerCase() }) : revisionWord(share.revisionOrdinal)}
      dek={shareDek(share)}
      meta={shareMeta(share)}
      {...(actions === undefined ? {} : { actions })}
    />
    <Dialog
      open={confirmingRevoke}
      title={portalText("core", "account_admin.revoke_ce463c", { value0: shareTitle(share) })}
      onClose={() => setConfirmingRevoke(false)}
      actions={
        <>
          <Button variant="secondary" onClick={() => setConfirmingRevoke(false)}>{portalText("core", "account_admin.keep_sharing_d8065c")}</Button>
          <Button
            variant="primary"
            disabled={busy}
            onClick={() => {
              setConfirmingRevoke(false);
              run(props.onRevoke);
            }}
          >{portalText("core", "account_admin.revoke_0be720")}</Button>
        </>
      }
    >
      <p className="c-note">
        {share.resourceKind === "voice_agent_configuration"
          ? portalText("core", "account_admin.no_workspace_can_make_a_new_copy_of_it_copies_already_made_stay_w_456638")
          : portalText("core", "account_admin.workspaces_that_accepted_it_keep_the_revision_in_their_live_versi_85cbeb")}{" "}{portalText("core", "account_admin.this_cannot_be_undone_ad2526")}</p>
    </Dialog>
    </>
  );
}

/** "menu-autumn.pdf · Roma orders, revision 4" / "Roma orders — configuration, revision 4". */
function choiceLabel(one: ShareableResourceView): string {
  return one.resourceKind === "agent_document"
    ? portalText("core", "account_admin._revision_046434", { value0: one.label, value1: one.voiceAgentName, value2: String(one.revisionOrdinal) })
    : portalText("core", "account_admin._configuration_revision_6c8342", { value0: one.label, value1: String(one.revisionOrdinal) });
}

function ShareDialog(props: {
  readonly open: boolean;
  readonly view: ResourceSharingView;
  readonly onClose: () => void;
  readonly onShare: (
    from: string,
    chosen: ShareableResourceView,
    destination: { readonly workspaceId: string; readonly name: string } | null,
  ) => Promise<void>;
}): ReactNode {
  usePortalTranslation("core");
  const publishable = props.view.workspace.filter((one) => props.view.publishableWorkspaceId.includes(one.workspaceId) && one.state === "active");
  const [from, setFrom] = useState(publishable[0]?.workspaceId ?? "");
  const choices = props.view.shareable.filter((one) => one.workspaceId === from);
  const [what, setWhat] = useState("");
  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);
  const chosen = choices.find((one) => `${one.resourceKind}:${one.resourceId}` === what) ?? choices[0];
  const destinations = props.view.workspace.filter((one) => one.state === "active" && one.workspaceId !== from);
  const destination = destinations.find((one) => one.workspaceId === to) ?? null;
  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (chosen === undefined || from === "") return;
    setSending(true);
    void props.onShare(from, chosen, destination).finally(() => setSending(false));
  };
  return (
    <Dialog open={props.open} title={portalText("core", "account_admin.share_something_39acda")} onClose={props.onClose}>
      <form className="shr-dialog-form" onSubmit={submit}>
        <p className="shr-dialog-text">{portalText("core", "account_admin.choose_a_document_or_an_agent_s_configuration_then_the_workspace_a42a57")}</p>
        <Field label={portalText("core", "account_admin.from_3f6605")}>
          {(control) => (
            <Select
              {...control}
              value={from}
              onChange={(event) => {
                setFrom(event.target.value);
                setWhat("");
                setTo("");
              }}
            >
              {publishable.map((one) => (
                <option key={one.workspaceId} value={one.workspaceId}>
                  {one.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={portalText("core", "account_admin.what_97211a")}>
          {(control) => (
            <Select {...control} value={chosen === undefined ? "" : `${chosen.resourceKind}:${chosen.resourceId}`} onChange={(event) => setWhat(event.target.value)}>
              {choices.map((one) => (
                <option key={`${one.resourceKind}:${one.resourceId}`} value={`${one.resourceKind}:${one.resourceId}`}>
                  {choiceLabel(one)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={portalText("core", "account_admin.with_564f8c")}>
          {(control) => (
            <Select {...control} value={to} onChange={(event) => setTo(event.target.value)}>
              <option value="">{portalText("core", "account_admin.every_other_workspace_b25295")}</option>
              {destinations.map((one) => (
                <option key={one.workspaceId} value={one.workspaceId}>
                  {one.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <p className="shr-dialog-text">
          {chosen?.resourceKind === "voice_agent_configuration"
            ? portalText("core", "account_admin.the_receiving_workspace_gets_its_own_copy_to_start_a_new_agent_fr_ed95f0")
            : portalText("core", "account_admin.the_receiving_workspace_can_add_this_revision_to_its_agents_docum_3e8510")}
        </p>
        <div className="shr-dialog-actions">
          <Button variant="primary" type="submit" disabled={sending || chosen === undefined}>{portalText("core", "account_admin.share_09ca55")}</Button>
        </div>
      </form>
    </Dialog>
  );
}
